//@flow

import * as React from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { IconButtonTheme } from './theme';

type IconButtonProps = {
  /**
   * This should pass a Birch icon component sourced from the Seedling library.
   */
  icon: React.AbstractComponent<any, any>,
  /**
   * Provides a label for the button.
   */
  label: string,
  /**
   * Fires when the button is clicked.
   */
  onClick: function,
  /**
   * Provides two sizing options for the button.
   */
  size?: 'sm' | 'md',
};

type IconButtonLinkProps = {|
  /**
   * This should pass a Birch icon component sourced from the Seedling library.
   */
  icon: React.AbstractComponent<any, any>,
  /**
   * Provides a label for the button.
   */
  label: string,
  /**
   * The link
   */
  href: string,
  /**
   * Provides two sizing options for the button.
   */
  size?: 'sm' | 'md',
|};

const SIZE_MAP = {
  sm: '16',
  md: '24',
};

const IconButton = (props: IconButtonProps) => {
  const { icon, size, label, onClick } = props;

  return (
    <IconButtonWrapper size={size || 'md'} aria-label={label} onClick={onClick}>
      {React.createElement(icon, {
        color: IconButtonTheme.iconColor,
        size: size,
      })}
    </IconButtonWrapper>
  );
};
IconButton.displayName = 'IconButton';

const IconButtonLink = ({ href, icon, label, size }: IconButtonLinkProps) => {
  return (
    <IconButtonWrapper
      size={size || 'md'}
      aria-label={label}
      as="a"
      href={href}
    >
      {React.createElement(icon, {
        color: IconButtonTheme.iconColor,
        size: size,
      })}
    </IconButtonWrapper>
  );
};
IconButtonLink.displayName = 'IconButtonLink';

const IconButtonExternalLink = ({
  href,
  icon,
  label,
  size,
}: IconButtonLinkProps) => {
  return (
    <IconButtonWrapper
      size={size || 'md'}
      aria-label={label}
      as="a"
      target="_blank"
      rel="noopener norefferer"
      href={href}
    >
      {React.createElement(icon, {
        color: IconButtonTheme.iconColor,
        size: size,
      })}
    </IconButtonWrapper>
  );
};
IconButtonExternalLink.displayName = 'IconButtonExternalLink';

const IconButtonWrapper: StyledComponent<
  { size: 'sm' | 'md' },
  *,
  *
> = styled.span`
  display: flex;
  padding: 4px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-sizing: content-box;

  ${(props) => `
    height: ${SIZE_MAP[props.size]}px;
    width: ${SIZE_MAP[props.size]}px;
  `}

  &:hover,
  &:focus-within {
    background: ${IconButtonTheme.hover.backgroundColor};
  }

  &:active {
    background: ${IconButtonTheme.active.backgroundColor};
  }

  cursor: pointer;
`;

export default IconButton;
export {
  IconButton,
  IconButtonLink,
  IconButtonExternalLink,
  IconButtonWrapper,
};
